import styled from "@emotion/styled";
import { CircularProgress } from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import { useAtomValue } from "jotai";
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { AuthContext } from "@/auth/AuthContext";
import { customFirestore } from "@/firebase";
import { companyDataAtom } from "@/routes/test-run/test-run-atoms";
import { type CompanyData } from "@/utils/types";

const PLATFORM_APP_URL: string = (
  (import.meta.env.VITE_PLATFORM_APP_URL as string | undefined) ??
  "https://platform.mobileboost.io"
).replace(/\/+$/, "");

// Same reading of companies/{name}.testsBackend as the backend's
// app/tests_backend.py: anything but "platform" means QA Studio.
const usesPlatformTests = (companyData?: CompanyData) =>
  typeof companyData?.testsBackend === "string" &&
  companyData.testsBackend.trim().toLowerCase() === "platform";

type Target = "list" | "session" | "home";

/**
 * Sends organisations on the MobileBoost Platform (testsBackend "platform")
 * from the SDK session pages to the same page on the Platform, or to its
 * homepage when the Platform has no such page. Staff (superUser) stay here.
 *
 * Children only render once the redirect is ruled out, so a platform org
 * never mounts the QA Studio page in between.
 */
const PlatformSessionsRedirect = ({
  target,
  children,
}: {
  target: Target;
  children: React.ReactNode;
}) => {
  const { user } = useContext<any>(AuthContext);
  const { sessionId } = useParams();
  const { data: companyData, isPending } = useAtomValue(
    companyDataAtom(user?.companyId)
  );

  const shouldRedirect =
    !!user?.companyId &&
    user.role !== "superUser" &&
    usesPlatformTests(companyData);
  const [destination, setDestination] = useState<string | null>(null);

  useEffect(() => {
    if (!shouldRedirect) return;
    if (target === "list") {
      setDestination(`${PLATFORM_APP_URL}/sessions`);
      return;
    }
    if (target === "home" || !sessionId) {
      setDestination(`${PLATFORM_APP_URL}/`);
      return;
    }
    let cancelled = false;
    void getDoc(doc(customFirestore, "sessions", sessionId))
      .then((snapshot) => snapshot.exists())
      .catch(() => false)
      .then((exists) => {
        if (cancelled) return;
        setDestination(
          exists
            ? `${PLATFORM_APP_URL}/sessions/${encodeURIComponent(sessionId)}`
            : `${PLATFORM_APP_URL}/`
        );
      });
    return () => {
      cancelled = true;
    };
  }, [shouldRedirect, target, sessionId]);

  useEffect(() => {
    if (destination) window.location.replace(destination);
  }, [destination]);

  if (user?.companyId && isPending) return <Spinner />;
  if (shouldRedirect) return <Spinner />;
  return <>{children}</>;
};

const Spinner = () => (
  <SpinnerWrapper>
    <CircularProgress />
  </SpinnerWrapper>
);

const SpinnerWrapper = styled.div`
  display: grid;
  place-content: center;
  height: 100%;
  flex: 1;
`;

export { PlatformSessionsRedirect };
